import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Shield, AlertTriangle, Plus, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DNRManagement = () => {
  const [dnrEntries, setDNREntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [documentNumber, setDocumentNumber] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDNREntries();
  }, []);

  const getToken = () => localStorage.getItem('access_token');

  const fetchDNREntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dnr`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDNREntries(response.data);
    } catch (error) {
      console.error('Error fetching DNR entries:', error);
      toast.error('Failed to load DNR list');
    } finally {
      setLoading(false);
    }
  };

  const addDNREntry = async () => {
    if (!documentNumber.trim() || !reason.trim()) {
      toast.error('Document number and reason are required');
      return;
    }

    try {
      await axios.post(`${API}/dnr`, {
        document_number: documentNumber,
        reason,
        notes
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Added to Do Not Rent list');
      setOpenDialog(false);
      setDocumentNumber('');
      setReason('');
      setNotes('');
      fetchDNREntries();
    } catch (error) {
      console.error('Error adding DNR entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to add DNR entry');
    }
  };

  const toggleDNRStatus = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/dnr/${id}`, null, {
        params: { active: !currentStatus },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(currentStatus ? 'DNR entry deactivated' : 'DNR entry activated');
      fetchDNREntries();
    } catch (error) {
      console.error('Error updating DNR entry:', error);
      toast.error('Failed to update DNR entry');
    }
  };

  const deleteDNREntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this DNR entry?')) return;

    try {
      await axios.delete(`${API}/dnr/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('DNR entry deleted');
      fetchDNREntries();
    } catch (error) {
      console.error('Error deleting DNR entry:', error);
      toast.error('Failed to delete DNR entry');
    }
  };

  const filteredEntries = dnrEntries.filter(entry => 
    entry.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent flex items-center gap-2">
            <Shield className="text-red-600" size={32} />
            Do Not Rent List
          </h1>
          <p className="text-slate-600 mt-2">
            Manage guests who are flagged and not allowed to make reservations
          </p>
        </div>
        
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-red-600 to-orange-600 gap-2">
              <Plus size={18} />
              Add to DNR List
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Do Not Rent Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="document-number">Document Number (ID/Passport)</Label>
                <Input
                  id="document-number"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  placeholder="e.g., ABC123456"
                />
              </div>
              <div>
                <Label htmlFor="reason">Reason</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Property damage, Chargeback"
                />
              </div>
              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              <Button onClick={addDNREntry} className="w-full">
                Add to DNR List
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 text-slate-400" size={18} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by document number or reason..."
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Entries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dnrEntries.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Flags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {dnrEntries.filter(e => e.active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Inactive</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-400">
              {dnrEntries.filter(e => !e.active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DNR List */}
      <Card>
        <CardHeader>
          <CardTitle>DNR Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-600">Loading...</p>
          ) : filteredEntries.length > 0 ? (
            <div className="space-y-3">
              {filteredEntries.map((entry) => (
                <div key={entry.id} className="p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-bold text-lg">{entry.document_number}</p>
                        <Badge variant={entry.active ? "destructive" : "outline"}>
                          {entry.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <AlertTriangle size={16} className="text-red-600" />
                          <span className="font-medium">Reason:</span>
                          <span className="text-slate-600">{entry.reason}</span>
                        </div>
                        {entry.notes && (
                          <p className="text-slate-600">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </p>
                        )}
                        <p className="text-xs text-slate-500">
                          Added: {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleDNRStatus(entry.id, entry.active)}
                      >
                        {entry.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteDNREntry(entry.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Shield className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-600">No DNR entries found</p>
              <p className="text-sm text-slate-500 mt-2">
                Guests flagged as Do Not Rent will appear here
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DNRManagement;